import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { customizationApi, type CustomizationConfig } from '../api/customizationApi';
import { useAuth } from './AuthContext';

type CustomizationContextValue = {
  config: CustomizationConfig | null;
  loading: boolean;
  refresh: () => Promise<void>;
  isModuleEnabled: (key: string) => boolean;
  isSectionVisible: (key: string) => boolean;
  widgetsForMe: string[];
};

const defaultValue: CustomizationContextValue = {
  config: null,
  loading: true,
  refresh: async () => undefined,
  isModuleEnabled: () => true,
  isSectionVisible: () => true,
  widgetsForMe: [],
};

const CustomizationContext = createContext<CustomizationContextValue>(defaultValue);

export const CustomizationProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [config, setConfig] = useState<CustomizationConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setConfig(null);
      setLoading(false);
      return;
    }
    try {
      const response = await customizationApi.me();
      setConfig(response.data);
    } catch {
      // Falling back to defaults: everything ON, no curated widget list.
      // The frontend has its own per-role hardcoded fallback, so the user
      // still sees a working dashboard when /customization/me is unavailable.
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  // Memoise so consumers don't re-render on every parent render — the value
  // identity only changes when config/loading/refresh actually change.
  const value = useMemo<CustomizationContextValue>(
    () => ({
      config,
      loading,
      refresh,
      isModuleEnabled: (key) => (config ? config.modules[key] !== false : true),
      isSectionVisible: (key) => (config ? config.sections[key] !== false : true),
      widgetsForMe: config?.dashboard_widgets ?? [],
    }),
    [config, loading, refresh],
  );

  return <CustomizationContext.Provider value={value}>{children}</CustomizationContext.Provider>;
};

export const useCustomization = () => useContext(CustomizationContext);
